import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, EmptyState, ListSkeleton } from "@/components/common";
import { TASK_STATUSES, PRIORITIES, item } from "@/lib/constants";
import { fmtDate, isOverdue, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import KanbanBoard from "@/components/KanbanBoard";
import TaskDialog from "@/components/TaskDialog";
import { Plus, Search, LayoutGrid, List, ListTodo } from "lucide-react";

export default function Tasks() {
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [presetStatus, setPresetStatus] = useState(null);

  const { data = [], isLoading } = useQuery({ queryKey: ["tasks"], queryFn: async () => { const d = (await api.get("/tasks")).data; return Array.isArray(d) ? d : []; } });
  const { data: members = [] } = useQuery({ queryKey: ["members"], queryFn: async () => (await api.get("/org/members")).data });
  const membersMap = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m.name])), [members]);

  useEffect(() => {
    const id = params.get("id");
    if (id) { setEditId(id); setDialogOpen(true); }
  }, [params]);

  const filtered = useMemo(() => data.filter((t) => {
    if (q && !t.title.toLowerCase().includes(q.toLowerCase())) return false;
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
    return true;
  }), [data, q, statusFilter, priorityFilter]);

  const openTask = (id) => { setEditId(id); setPresetStatus(null); setDialogOpen(true); };
  const addTask = (status) => { setEditId(null); setPresetStatus(status); setDialogOpen(true); };
  const closeDialog = (v) => { if (!v) { setDialogOpen(false); setEditId(null); setPresetStatus(null); if (params.get("id")) setParams({}); } };

  return (
    <div>
      <PageHeader title="Görevler" subtitle={`${filtered.length} görev`}
        actions={<Button onClick={() => addTask(null)} data-testid="new-task-button"><Plus className="h-4 w-4 mr-1.5" /> Yeni Görev</Button>} />

      <div className="flex flex-col md:flex-row gap-2 mb-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Görev ara..." className="pl-9 h-9" data-testid="task-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-9" data-testid="task-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm durumlar</SelectItem>{TASK_STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-36 h-9" data-testid="task-priority-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tüm öncelikler</SelectItem>{PRIORITIES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex rounded-lg border p-0.5 ml-auto h-9">
          <Button variant={view === "kanban" ? "secondary" : "ghost"} size="sm" onClick={() => setView("kanban")} data-testid="view-kanban"><LayoutGrid className="h-4 w-4" /></Button>
          <Button variant={view === "list" ? "secondary" : "ghost"} size="sm" onClick={() => setView("list")} data-testid="view-list"><List className="h-4 w-4" /></Button>
        </div>
      </div>

      {isLoading ? <ListSkeleton rows={6} /> : filtered.length === 0 ? (
        <EmptyState icon={ListTodo} title="Görev bulunamadı" description="Yeni bir görev oluşturarak başlayın." action={<Button onClick={() => addTask(null)}><Plus className="h-4 w-4 mr-1.5" /> Yeni Görev</Button>} />
      ) : view === "kanban" ? (
        <KanbanBoard tasks={filtered} onOpen={openTask} onAdd={addTask} membersMap={membersMap} />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow><TableHead>#</TableHead><TableHead>Başlık</TableHead><TableHead>Durum</TableHead><TableHead>Öncelik</TableHead><TableHead>Atananlar</TableHead><TableHead>Bitiş</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((t) => {
                const st = item(TASK_STATUSES, t.status), pr = item(PRIORITIES, t.priority);
                return (
                  <TableRow key={t.id} className="cursor-pointer" onClick={() => openTask(t.id)} data-testid={`task-row-${t.id}`}>
                    <TableCell className="font-mono text-xs text-muted-foreground">#{t.number}</TableCell>
                    <TableCell className="font-medium max-w-xs truncate">{t.title}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-[10px] ${st.chip}`}>{st.label}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className={`text-[10px] ${pr.chip}`}>{pr.label}</Badge></TableCell>
                    <TableCell><div className="flex -space-x-1.5">{(t.assignees || []).slice(0, 3).map((a, i) => <Avatar key={i} className="h-6 w-6 border-2 border-card"><AvatarFallback className="text-[8px]">{initials(membersMap[a])}</AvatarFallback></Avatar>)}</div></TableCell>
                    <TableCell className={`text-sm ${isOverdue(t.due_date, t.status) ? "text-rose-600 font-medium" : "text-muted-foreground"}`}>{fmtDate(t.due_date)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <TaskDialog open={dialogOpen} onOpenChange={closeDialog} taskId={editId} defaultProject={null} key={editId || presetStatus || "new"} />
    </div>
  );
}
